import React from 'react';
import { Pressable, View } from 'react-native';

import { Stack } from '@/components/layout';
import { SheetContent, SheetModal, SvgIcon, Text } from '@/components/ui';
import Modal from '@/components/ui/modal/modal';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

const strings = STRINGS.coach.notifications.settings;

type CoachNotificationSettingsSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPreferences: () => void;
  onMarkAllRead: () => void;
};

type SettingsRow = {
  key: string;
  label: string;
  icon: 'IcSetting3' | 'IcMessageSquareCheck';
  onPress: () => void;
};

/** Settings panel opened from the `···` header button (Scenario 4). */
export function CoachNotificationSettingsSheet({
  open,
  onOpenChange,
  onPreferences,
  onMarkAllRead,
}: CoachNotificationSettingsSheetProps) {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();

  const rows: SettingsRow[] = [
    {
      key: 'preferences',
      label: strings.notificationPreferences,
      onPress: onPreferences,
      icon: 'IcSetting3',
    },
    {
      key: 'markAllRead',
      label: strings.markAllAsRead,
      onPress: onMarkAllRead,
      icon: 'IcMessageSquareCheck',
    },
  ];

  const close = () => onOpenChange(false);

  const content = (
    <Stack paddingX={scale(20)} paddingTop={scale(16)} paddingBottom={scale(12)}>
      <Stack direction="row" align="center" justify="flex-end">
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={strings.close}
          hitSlop={12}
          onPress={close}
          style={styles.closeButton}>
          <SvgIcon name="IcXClose" size={scale(20)} color={colors['10']} />
        </Pressable>
      </Stack>

      <Stack marginTop={scale(8)} gap={scale(4)}>
        {rows.map(row => (
          <Pressable
            key={row.key}
            accessibilityRole="button"
            accessibilityLabel={row.label}
            onPress={row.onPress}
            style={({ pressed }) => [styles.row, pressed ? styles.pressed : null]}>
            <SvgIcon name={row.icon} size={scale(24)} color={guardianColors['6']} />
            <Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold" style={styles.rowLabel}>
              {row.label}
            </Text>
          </Pressable>
        ))}
      </Stack>
    </Stack>
  );

  if (isTablet) {
    return (
      <Modal
        visible={open}
        onBackdropPress={close}
        onBackButtonPress={close}
        contentContainerStyle={styles.modalCenter}>
        <View style={styles.modalCard}>{content}</View>
      </Modal>
    );
  }

  return (
    <SheetModal open={open} onOpenChange={onOpenChange}>
      <SheetContent closeOnBackdropPress style={styles.sheet}>
        {content}
      </SheetContent>
    </SheetModal>
  );
}

const useStyles = createStyles(({ scale, deviceWidth }) => ({
  sheet: { backgroundColor: colors['0'] },
  modalCenter: {
    paddingHorizontal: scale(24),
  },
  modalCard: {
    // Explicit width — `width: '100%'` collapses inside the centered Modal wrapper
    // (parent has no measured width), which hid the row labels and left only icons.
    width: Math.min(scale(380), deviceWidth * 0.9),
    backgroundColor: colors['0'],
    borderRadius: scale(16),
    overflow: 'hidden',
  },
  closeButton: {
    width: scale(32),
    height: scale(32),
    borderRadius: scale(16),
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors['3'],
  },
  row: {
    minHeight: scale(52),
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(12),
  },
  rowLabel: { flex: 1, color: colors['10'] },
  pressed: { opacity: 0.68 },
}));

export default CoachNotificationSettingsSheet;
